<template>
    <v-card class="traps text--primary">
        <v-toolbar color="primary" dark>
            <v-app-bar-nav-icon>
                <v-progress-circular
                    :size="28"
                    :width="3"
                    indeterminate
                    color="white"
                    v-if="!showRefresh && $apollo.loading"
                ></v-progress-circular>
                <v-icon v-else>inbox</v-icon>
            </v-app-bar-nav-icon>

            <v-toolbar-title>{{ title }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <template v-if="showRefresh">
                <v-app-bar-nav-icon v-if="$apollo.loading">
                    <v-progress-circular
                        :size="28"
                        :width="3"
                        indeterminate
                        color="white"
                    ></v-progress-circular>
                </v-app-bar-nav-icon>
                <v-btn icon v-else @click="refresh">
                    <v-icon>cached</v-icon>
                </v-btn>
            </template>

            <v-btn v-if="showNew" rounded dark color="green" :to="{ name: 'trap-new' }">
                {{ $t("button.new") }}
                <v-icon right>add_circle</v-icon>
            </v-btn>
        </v-toolbar>
        <v-card-text class="text--primary">
            <StatusHelper />
            <TrapFilter
                v-if="showFilter"
                :categories.sync="categoryFilter"
                :plan.sync="planFilter"
                :status.sync="statusFilter"
            />
            <div
                class="headline text-xs-center mt-5"
                v-if="(!allTraps || allTraps.length <= 0) && !$apollo.loading"
            >
                {{ $t("message.no_results") }}
                <v-btn
                    v-if="!$apollo.loading && !planFilter && !statusFilter && !categoryFilter"
                    rounded
                    dark
                    block
                    color="green"
                    :to="{ name: 'trap-new' }"
                >
                    {{ $t("button.add_trap") }}
                    <v-icon right>add_circle</v-icon>
                </v-btn>
            </div>
        </v-card-text>
        <v-card-text class="text--primary" v-if="pagination && trapPages && trapPages.pages">
            <BasePagination
                :page="page"
                @next="nextPage"
                @previous="previousPage"
                :total="trapPages.total"
                :loading="$apollo.queries.trapPages.loading"
                :pages="trapPages.pages"
            />
        </v-card-text>
        <v-list two-line>
            <v-list-group v-for="trap in allTraps" :key="trap._id" v-model="trap.active" no-action>
                <template v-slot:activator>
                    <v-list-item-content>
                        <v-list-item-title class="trap-title font-weight-bold"
                            >{{ trap.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                            <template v-if="trap.status">
                                <v-chip
                                    color="red"
                                    text-color="white"
                                    v-if="noStatusForPast24h(trap.status.timeStamp)"
                                    :key="'alert' + trap._id + minuteTime"
                                >
                                    <v-avatar style="margin-left: -5px;">
                                        <v-icon>warning</v-icon>
                                    </v-avatar>
                                    {{ $t("status.alert.no_status_last_24h") }}
                                </v-chip>
                                <v-chip
                                    small
                                    v-if="trap.status.right && trap.status.left"
                                    color="green"
                                    class="chip-status"
                                    text-color="white"
                                    >{{ $t("status.armed") }}</v-chip
                                >
                                <v-chip
                                    small
                                    v-else-if="!trap.status.right && !trap.status.left"
                                    color="red"
                                    class="chip-status"
                                    text-color="white"
                                    >{{ $t("status.triggered") }} 2/2</v-chip
                                >
                                <v-chip
                                    small
                                    v-else-if="!trap.status.right || !trap.status.left"
                                    color="orange"
                                    class="chip-status"
                                    text-color="white"
                                    >{{ $t("status.triggered") }} 1/2</v-chip
                                >

                                <v-chip
                                    small
                                    class="chip-battery hidden-sm-and-down"
                                    outlined
                                    :text-color="getBatteryColor(trap.status.battery)"
                                >
                                    <v-avatar>
                                        <v-icon>battery_full</v-icon>
                                    </v-avatar>
                                    {{ trap.status.battery }}%
                                </v-chip>
                                <v-chip
                                    small
                                    outlined
                                    class="chip-battery hidden-sm-and-down"
                                    :text-color="getBatteryColor(trap.status.signal)"
                                >
                                    <v-avatar>
                                        <v-icon>signal_cellular_alt</v-icon>
                                    </v-avatar>
                                    {{ trap.status.signal }}%
                                </v-chip>
                            </template>
                            <v-chip outlined v-else class="chip-status" small>{{
                                $t("status.no")
                            }}</v-chip>
                            <br />
                            <v-chip
                                v-for="category in trap.categories"
                                :key="category._id"
                                outlined
                                small
                                color="black"
                                label
                                >{{ category.name }}</v-chip
                            >
                            <template v-if="trap.status">
                                <br />
                                <span>{{ $t("label.last_status_received") }}:</span>
                                {{
                                    trap.status.timeStamp
                                        | dateFormat(
                                            {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                            },
                                            fullLocale
                                        )
                                }}
                            </template>
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </template>

                <TrapDetail :trap="trap" />
            </v-list-group>
        </v-list>
        <v-card-text class="text--primary">
            <BasePagination
                v-if="pagination && trapPages && trapPages.pages"
                :page="page"
                @next="nextPage"
                @previous="previousPage"
                :total="trapPages.total"
                :pages="trapPages.pages"
                :loading="$apollo.queries.trapPages.loading"
            />
        </v-card-text>
        <v-card-text>
            <v-layout>
                <v-spacer></v-spacer>
                <v-btn v-if="!showNew" rounded dark color="green" :to="{ name: 'trap-new' }">
                    {{ $t("button.new") }}
                    <v-icon right>add_circle</v-icon>
                </v-btn>
                <v-btn v-if="showSeeall" :to="{ name: 'traps' }">{{
                    $t("button.all_traps")
                }}</v-btn>
            </v-layout>
        </v-card-text>
    </v-card>
</template>

<script>
import StatusHelper from "./StatusHelper"
import TrapDetail from "./TrapDetail"
import TrapFilter from "./TrapFilter"
import loadingMixin from "@/mixins/loading"
import { mapGetters } from "vuex"
export default {
    mixins: [loadingMixin],
    components: { TrapFilter, TrapDetail, StatusHelper },
    props: {
        title: {
            type: String,
            default() {
                return this.$i18n.t("title.traps")
            },
        },
        sortBy: { type: String, default: "name" },
        showFilter: { type: Boolean, default: false },
        pagination: { type: Boolean, default: true },
        maxResult: { type: Number, default: 20 },
        showSeeall: { type: Boolean, default: false },
        showNew: { type: Boolean, default: false },
        showRefresh: { type: Boolean, default: false },
        filterStatus: { type: String, default: "all" },
        filterCategory: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            categoryFilter: [],
            page: 1,
            planFilter: null,
            traps: null,
            trapPages: null,
            statusFilter: null,
            currentActiveTrap: null,
            minuteTime: new Date().getTime() + Math.random(),
            interval: null,
        }
    },
    methods: {
        noStatusForPast24h(time) {
            return new Date().getTime() - 25 * 60 * 60 * 1000 > time
        },
        getBatteryColor(percent) {
            if (!percent) {
                return ""
            }

            if (percent > 60) {
                return "green"
            }
            if (percent > 40) {
                return "amber darken-1"
            }
            if (percent > 20) {
                return "orange"
            }
            return "red"
        },
        startPolling() {
            this.$apollo.queries.traps.startPolling(20000)
        },
        stopPolling() {
            this.$apollo.queries.traps.stopPolling()
        },

        refresh() {
            this.$apollo.queries.traps.refresh()
        },
        previousPage() {
            if (!this.$apollo.queries.trapPages.loading && this.page > 1) {
                this.page = this.page - 1
            }
        },
        nextPage() {
            if (!this.$apollo.queries.trapPages.loading && this.page < this.trapPages.pages) {
                this.page = this.page + 1
            }
        },
    },
    computed: {
        allTraps() {
            return this.traps || (this.trapPages ? this.trapPages.docs : null)
        },
        ...mapGetters({ fullLocale: "user/fullLocale" }),
    },
    watch: {
        categoryFilter() {
            this.page = 1
        },
        planFilter() {
            this.page = 1
        },
        statusFilter() {
            this.page = 1
        },
    },
    activated() {
        if (this.pagination) {
            this.$apollo.queries.trapPages.skip = false
        } else {
            this.$apollo.queries.traps.skip = false
        }
    },
    deactivated() {
        if (this.pagination) {
            this.$apollo.queries.trapPages.skip = true
        } else {
            this.$apollo.queries.traps.skip = true
        }
    },
    mounted() {
        if (this.pagination) {
            setTimeout(() => {
                this.$apollo.queries.trapPages.skip = false
            }, 1)
        } else {
            setTimeout(() => {
                this.$apollo.queries.traps.skip = false
            }, 1)
        }

        this.interval = setInterval(() => {
            this.minuteTime += 1
        }, 20000)
        if (this.filterStatus) {
            this.statusFilter = this.filterStatus
        }
        if (this.filterCategory) {
            this.categoryFilter = this.filterCategory
        }
    },

    beforeDestroy() {
        clearInterval(this.interval)
        this.$apollo.queries.traps.stopPolling()
    },
    apollo: {
        trapPages: {
            loadingKey: "loadingTrap",
            query: require("@/graphql/trap/trapPages.gql"),
            variables() {
                return {
                    sort: this.sortBy,
                    categories: this.categoryFilter,
                    status: this.statusFilter == "all" ? "" : this.statusFilter,
                    plan: this.planFilter,
                    page: this.page,
                }
            },
            pollInterval: 20000,
            skip: true,
            update({ trapPages }) {
                if (trapPages.docs.length <= 0) {
                    this.page = 1
                }
                return trapPages
            },
        },
        traps: {
            loadingKey: "loadingTrap",
            query: require("@/graphql/trap/traps.gql"),
            variables() {
                return {
                    sort: this.sortBy,
                    categories: this.categoryFilter,
                    status: this.statusFilter == "all" ? "" : this.statusFilter,
                    plan: this.planFilter,
                    page: this.page,
                    maxResult: this.maxResult,
                }
            },
            pollInterval: 20000,
            skip: true,
            update({ traps }) {
                return traps
            },
        },
    },
}
</script>
<style lang="scss">
.traps {
    .v-list-group--active {
        background: #efefef91;
    }
    .serial-number {
        min-width: 167px;
        margin-left: 0;
        text-align: center;
    }
    .chip-status {
        min-width: 100px;
    }
    .chip-battery {
        min-width: 90px;
    }
    .huge {
        height: 130px;
    }

    .v-list {
        .v-list-group {
            border-bottom: 2px solid #999 !important;
        }
    }
}
</style>
